<template>
  <div class="space-y-8">
    <div class="sm:flex sm:items-center sm:justify-between">
      <div>
        <h1 class="text-base/7 font-semibold text-gray-900 dark:text-white">Metrics</h1>
        <p class="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
          Connections are destinations for the metrics of your runners: OpenTelemetry (OTLP/HTTP)
          endpoints, for example OpenObserve, Grafana Cloud or your own OTel Collector. Add one
          here, then choose it under Metrics, Send to, when you create or edit a runner. The
          collector on the VM pushes the CPU, RAM, swap, disk and job metrics there directly.
        </p>
      </div>
      <router-link
        v-if="canManage && canChange"
        :to="{ name: 'CreateMetricsConnection' }"
        class="mt-4 inline-flex shrink-0 items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:mt-0"
        data-testid="create-connection"
      >
        <PlusIcon class="-ml-0.5 size-5" aria-hidden="true" />
        New connection
      </router-link>
    </div>

    <p v-if="loading" class="text-sm text-gray-500 dark:text-gray-400">Loading connections…</p>
    <p v-else-if="loadError" class="text-sm text-red-600 dark:text-red-400" role="alert">
      {{ loadError }}
    </p>
    <div
      v-else-if="connections.length === 0"
      class="rounded-lg border border-dashed border-gray-300 p-8 text-center dark:border-white/15"
      data-testid="connections-empty"
    >
      <ChartBarIcon class="mx-auto size-10 text-gray-400" aria-hidden="true" />
      <p class="mt-2 text-sm font-semibold text-gray-900 dark:text-white">No connections yet</p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Add the OTLP endpoint of your observability stack, then send metrics there.
      </p>
    </div>
    <ul
      v-else
      role="list"
      class="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 dark:divide-white/10 dark:bg-gray-800/50 dark:ring-white/10"
    >
      <li
        v-for="connection in connections"
        :key="connection.id"
        class="flex flex-col gap-4 px-4 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6"
        data-testid="connection-row"
      >
        <div class="min-w-0 space-y-1">
          <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ connection.name }}</p>
          <p class="truncate text-sm text-gray-500 dark:text-gray-400" :title="connection.endpoint">
            {{ connection.endpoint
            }}{{
              connection.headers.length
                ? ` · ${connection.headers.map((h) => h.name).join(', ')}`
                : ''
            }}
          </p>
          <div class="text-sm text-gray-500 dark:text-gray-400" data-testid="connection-used-by">
            <template v-if="connection.usedBy.length === 0 && !externalOf(connection).length"
              >Not used by any runner</template
            >
            <template v-else>
              Used by
              <template v-for="(runner, index) in connection.usedBy" :key="runner.runnerId">
                <span class="font-medium text-gray-700 dark:text-gray-300">{{ runner.name }}</span>
                <span class="text-gray-400"> ({{ stateLabel(runner.state) }})</span
                ><template v-if="index < connection.usedBy.length - 1">, </template>
              </template>
            </template>
            <template v-if="externalOf(connection).length">
              <template v-if="connection.usedBy.length">; </template>external runners
              <span
                class="font-medium text-gray-700 dark:text-gray-300"
                data-testid="connection-external"
                >{{ externalNames(connection) }}</span
              >
            </template>
          </div>
        </div>
        <div v-if="canManage" class="flex shrink-0 gap-3">
          <router-link
            :to="{ name: 'EditMetricsConnection', params: { id: connection.id } }"
            class="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20"
            :data-testid="`edit-connection-${connection.id}`"
          >
            {{ canChange ? 'Edit' : 'View' }}
          </router-link>
          <button
            type="button"
            class="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-red-600 shadow-xs inset-ring inset-ring-gray-300 hover:bg-red-50 dark:bg-white/10 dark:text-red-400 dark:inset-ring-white/5 dark:hover:bg-white/20 cursor-pointer"
            :data-testid="`delete-connection-${connection.id}`"
            @click="askDelete(connection)"
          >
            Delete
          </button>
        </div>
      </li>
    </ul>

    <ConfirmDialog
      :open="deleting !== null && recreate === null"
      :title="`Delete ${deleting?.name ?? 'connection'}?`"
      :message="deleteMessage"
      confirm-label="Delete"
      busy-label="Deleting…"
      :busy="deleteBusy"
      :error="deleteError"
      @confirm="remove(false)"
      @cancel="deleting = null"
    />

    <RecreateRunnersDialog
      :runners="recreate"
      message="Deleting the connection stops these runners from sending metrics to it and recreates the VMs of the active ones without it."
      confirm-label="Delete and recreate"
      :busy="deleteBusy"
      @confirm="remove(true)"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { ChartBarIcon, PlusIcon } from '@heroicons/vue/24/outline';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import RecreateRunnersDialog from '@/components/RecreateRunnersDialog.vue';
import { asProblem, conflictRunners } from '@/api/caches';
import {
  deleteMetricsConnection,
  listMetricsConnections,
  type MetricsConnection,
  type MetricsRunner,
} from '@/api/metrics-connections';
import { useMonitoringEntitlement } from '@/lib/monitoring-entitlement';
import { useOrganizationStore } from '@/stores/organization';
import { useToastStore } from '@/stores/toast';

const organizations = useOrganizationStore();
const toast = useToastStore();
// Deleting needs no active plan, so stored credentials can be removed after the plan ends.
const canManage = computed(() => organizations.canManageRunners);
const { canChange } = useMonitoringEntitlement();

const connections = ref<MetricsConnection[]>([]);
const loading = ref(true);
const loadError = ref('');

const deleting = ref<MetricsConnection | null>(null);
const recreate = ref<MetricsRunner[] | null>(null);
const deleteBusy = ref(false);
const deleteError = ref<string | null>(null);

function stateLabel(state: string) {
  return state.toLowerCase().replace(/_/g, ' ');
}

async function load() {
  loading.value = true;
  loadError.value = '';
  try {
    connections.value = await listMetricsConnections();
  } catch (error) {
    const e = asProblem(error);
    loadError.value =
      e?.response?.data?.detail || e?.message || 'Failed to load the metrics connections.';
  } finally {
    loading.value = false;
  }
}

function externalOf(connection: MetricsConnection) {
  return connection.externalRunners ?? [];
}

function externalNames(connection: MetricsConnection) {
  return externalOf(connection)
    .map((r) => r.name)
    .join(', ');
}

const deleteMessage = computed(() => {
  const external = deleting.value ? externalOf(deleting.value) : [];
  return (
    'Runners that send their metrics to this connection stop sending them there, and their Send to becomes None. ' +
    'The stored headers are deleted.' +
    (external.length
      ? ` The agents of the external runners ${external.map((r) => r.name).join(', ')} stop sending, also to the ` +
        'dashboard, until you reinstall them with a new install command, or uninstall them.'
      : '')
  );
});

function askDelete(connection: MetricsConnection) {
  deleteError.value = null;
  recreate.value = null;
  deleting.value = connection;
}

function cancelDelete() {
  recreate.value = null;
  deleting.value = null;
}

async function remove(confirmed: boolean) {
  const connection = deleting.value;
  if (!connection) return;
  deleteBusy.value = true;
  deleteError.value = null;
  try {
    const result = await deleteMetricsConnection(connection.id, confirmed);
    const recreated = result.recreatedRunners.map((r) => r.name);
    const stopped = (result.stoppedExternalRunners ?? []).map((r) => r.name);
    toast.success(
      [
        'Metrics connection deleted.',
        recreated.length ? `Recreating ${recreated.join(', ')}.` : '',
        stopped.length
          ? `The agents of ${stopped.join(', ')} stopped sending until reinstalled.`
          : '',
      ]
        .filter(Boolean)
        .join(' ')
    );
    cancelDelete();
    await load();
  } catch (error) {
    const runners = conflictRunners(error, 'METRICS_CONNECTION_RECREATE_REQUIRED');
    if (runners && !confirmed) {
      recreate.value = runners;
      return;
    }
    recreate.value = null;
    const busy = conflictRunners(error, 'RUNNER_LOCKED');
    const e = asProblem(error);
    deleteError.value = busy?.length
      ? `These runners are busy, try again later: ${busy.map((r) => r.name).join(', ')}.`
      : e?.response?.data?.detail || e?.message || 'Failed to delete the metrics connection.';
  } finally {
    deleteBusy.value = false;
  }
}

onMounted(load);
</script>
